import { formatDate } from "@tokenterminal/ui/utils"
import { getMetricUnit } from "modules/metrics/getFormatUnit"
import { capitalize } from "utils/lodash/capitalize"
import type { ZoomValues } from "../Zoom/constants"
import type {
  ChartSerie,
  ChartSerieNonTimeData,
  ChartSerieTimeData,
} from "@tokenterminal/ui/Chart/useHighchartOptions"

function downloadBlob(blob: Blob, filename: string) {
  // Create an object URL for the blob object
  const url = URL.createObjectURL(blob)

  // Create a new anchor element
  const a = document.createElement("a")

  // Set the href and download attributes for the anchor element
  // You can optionally set other attributes like `title`, etc
  // Especially, if the anchor element will be attached to the DOM
  a.href = url
  a.download = filename || "download"

  // Click handler that releases the object URL after the element has been clicked
  // This is required for one-off downloads of the blob content
  function clickHandler(e: MouseEvent) {
    setTimeout(() => {
      URL.revokeObjectURL(url)
      e.currentTarget?.removeEventListener(
        "click",
        clickHandler as EventListener
      )
    }, 150)
  }
  a.addEventListener("click", clickHandler, false)
  a.click()
}

export function downloadChartDataBlob(info: string, data: string): void {
  const blob = new Blob([data], { type: "text/csv" })
  downloadBlob(
    blob,
    capitalize(info).replace(/[^\w )(]/gi, "") +
      " " +
      formatDate(new Date()) +
      ".csv"
  )
}

function dateToCsvFormat(timestamp: string) {
  return formatDate(new Date(Number.parseInt(timestamp, 10)))
}

function getColumnName(
  { yAxis, label, format }: ChartSerie,
  isTimeBased: boolean = false
) {
  const unit = getMetricUnit(format)
  const headerName = isTimeBased ? label : yAxis

  if (unit) {
    return `${headerName} (${unit})`
  }

  return headerName
}

function generateTimeSeriesData(
  chartSeries: Array<ChartSerie>,
  chartData: Record<string, ChartSerieTimeData>
) {
  const DELIMITER = ","
  const header = "Date"

  const dataByTimestamp: Record<number, Record<string, number | null>> = {}
  for (const serieName in chartData) {
    chartData[serieName].forEach(([timestamp, value]) => {
      dataByTimestamp[timestamp] = {
        ...dataByTimestamp[timestamp],
        [serieName]: value,
      }
    })
  }

  const csvHeaderRow = `${header},${chartSeries
    .map((serie) => getColumnName(serie, true))
    .join(DELIMITER)}`

  if (!Object.keys(dataByTimestamp).length) {
    return csvHeaderRow
  }

  // Object keys are unsorted thus we need to sort them for timelines
  // We sort only timestamp based data sets
  const rows: Array<string> = Array.from(Object.entries(dataByTimestamp))
    .sort(
      ([prevTimestamp], [nextTimestamp]) =>
        new Date(parseInt(prevTimestamp)).getTime() -
        new Date(parseInt(nextTimestamp)).getTime()
    )
    .map(
      ([key, rowData]) =>
        `${dateToCsvFormat(key)},${chartSeries
          .map((serie) => rowData[serie.name]?.toFixed(2))
          .join(",")}`
    )

  return `${csvHeaderRow}\n${rows.join("\n")}`
}

function generateSeriesData(
  chartSeries: Array<ChartSerie>,
  chartData: Record<string, ChartSerieNonTimeData>
) {
  const header = "Project"

  const csvHeaderRow = `${header},${getColumnName(chartSeries[0])}`

  const firstSerieData = chartData[Object.keys(chartData)[0]]

  // Object keys are unsorted thus we need to sort them for timelines
  // We sort only timestamp based data sets
  const rows: Array<string> = firstSerieData.map(
    ([key, rowData]) => `${key},${rowData.toFixed(2)}`
  )

  return `${csvHeaderRow}\n${rows.join("\n")}`
}

// Highcharts does not support exporting granularity
export function generateData<T extends boolean = boolean>(
  chartSeries: Array<ChartSerie>,
  chartData:
    | Record<string, ChartSerieNonTimeData>
    | Record<string, ChartSerieTimeData>,
  { isTimeBased }: { zoom?: ZoomValues; isTimeBased: T }
): string | undefined {
  if (!Object.keys(chartData).length || typeof isTimeBased === "undefined") {
    return `Data not loaded yet, please try again`
  }

  if (isTimeBased) {
    return generateTimeSeriesData(
      chartSeries,
      chartData as Record<string, ChartSerieTimeData>
    )
  } else {
    return generateSeriesData(
      chartSeries,
      chartData as Record<string, ChartSerieNonTimeData>
    )
  }
}
